import { useTranslation } from 'react-i18next';
import { Text } from '@getkarla/frontend-framework';
import { accountT } from '~/utils/account-settings-i18n';
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { Links, Meta, Outlet, Scripts, ScrollRestoration, isRouteErrorResponse, useLocation } from 'react-router';
import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { Provider, useSelector } from 'react-redux';
import i18next, { isPlatformLanguage, rememberPlatformLanguage } from '~/utils/i18n';
import { ToastRoot } from '~/components/toast';
import { ENV } from '~/utils/constants';

import reducers from './redux/reducers';
import { baseApi } from './api/base-api';
import { identityMiddleware } from './redux/identity-middleware';
import { IDENTITY_CLEAR } from './utils/identity-scope';

import './app.css';
import './assets/scss/main.scss';

const store = configureStore({
  reducer: reducers,
  middleware: (getDefault) =>
    getDefault({
      immutableCheck: false,
      serializableCheck: false,
    }).concat(baseApi.middleware, identityMiddleware),
  devTools: import.meta.env.VITE_APP_ENV !== 'production',
});

setupListeners(store.dispatch);

const getPageTitle = (pathname: string) => {
  if (pathname === '/' || pathname === '/agents' || pathname === '/models') return `${accountT('Agents')} | Karla`;
  if (pathname.startsWith('/agents/')) return `${accountT('Agent')} | Karla`;
  if (pathname === '/conversations') return `${accountT('Conversations')} | Karla`;
  if (pathname.startsWith('/conversations/')) return `${accountT('Conversation')} | Karla`;
  if (pathname === '/knowledge-base' || pathname === '/datastreams') return `${accountT('Knowledge base')} | Karla`;
  if (pathname.startsWith('/knowledge-base/') || pathname.startsWith('/datastreams/')) return `${accountT('Datastream')} | Karla`;
  if (pathname.startsWith('/settings')) return `${accountT('Settings')} | Karla`;
  if (pathname === '/assistant') return `${accountT('Karla Assistant')} | Karla`;
  if (pathname === '/help') return `${accountT('Help')} | Karla`;
  if (pathname === '/insights') return `${accountT('Insights')} | Karla`;
  if (pathname === '/wrapped') return 'Karla Wrapped';
  if (pathname === '/operations') return `${accountT('Operations')} | Karla`;
  if (pathname === '/users') return `${accountT('Users')} | Karla`;
  if (pathname === '/partners') return `${accountT('Partners')} | Karla`;
  if (pathname === '/organizations') return `${accountT('Organizations')} | Karla`;
  if (pathname === '/choose-organization') return `${accountT('Choose Organization')} | Karla`;
  if (pathname === '/login') return `${accountT('Login')} | Karla`;
  if (pathname === '/sign-up') return `${accountT('Sign Up')} | Karla`;
  if (pathname === '/reset-password') return `${accountT('Reset Password')} | Karla`;
  if (pathname === '/verify-email') return `${accountT('Verify Email')} | Karla`;
  if (pathname === '/accept-invite') return `${accountT('Accept Invite')} | Karla`;
  if (pathname === '/share') return `${accountT('Test Lab')} | Karla`;
  if (pathname === '/admin-consent/success') return `${accountT('Admin Consent Granted')} | Karla`;
  if (pathname === '/admin-consent/error') return `${accountT('Admin Consent Failed')} | Karla`;
  if (pathname === '/slack') return `${accountT('Slack Integration')} | Karla`;
  if (pathname === '/instagram') return `${accountT('Instagram Integration')} | Karla`;
  return 'Karla';
};

function DocumentTitle() {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();

  useEffect(() => {
    document.title = getPageTitle(pathname);
  }, [pathname, i18n.resolvedLanguage]);

  return null;
}

function UserLanguage() {
  const language = useSelector((state: any) => state.me?.user?.settings?.language);
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!isPlatformLanguage(language)) return;
    void i18next.changeLanguage(language);
    rememberPlatformLanguage(language);
  }, [language]);

  useEffect(() => {
    document.documentElement.lang = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  }, [i18n.resolvedLanguage, i18n.language]);

  return null;
}

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default function App() {
  useEffect(() => {
    const clear = () => store.dispatch({ type: IDENTITY_CLEAR });
    window.addEventListener('karla:identity-reset', clear);
    return () => window.removeEventListener('karla:identity-reset', clear);
  }, []);
  useEffect(() => {
    if (import.meta.env.VITE_APP_ENV === 'production') {
      const w = window as any;
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-P5T7M5Z';
      document.head.appendChild(script);
    }
    if (ENV !== 'production') {
      const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]') ?? document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/x-icon';
      link.href = `/favicon_${ENV}.ico`;
      if (!link.parentNode) document.head.appendChild(link);
    }
  }, []);

  return (
    <html lang="en" className="h-full">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="h-full">
        {/* ── Provider is ABOVE everything that needs Redux ── */}
        <Provider store={store}>
          <DocumentTitle />
          <UserLanguage />
          <Outlet />
          <ToastRoot />
        </Provider>

        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function HydrateFallback() {
  return (
    <html lang="en" className="h-full">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Links />
      </head>
      <body style={{ margin: 0, height: '100%', backgroundColor: '#f9f7f3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{`@keyframes karla-spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width: 32, height: 32, border: '3px solid #e8e8ee', borderTopColor: '#6a46ff', borderRadius: '50%', animation: 'karla-spin 0.7s linear infinite' }} />
        <Scripts />
      </body>
    </html>
  );
}

export function ErrorBoundary({ error }: { error: unknown }) {
  useTranslation();
  useEffect(() => {
    if (error instanceof Error) Sentry.captureException(error);
  }, [error]);

  let message = accountT('Oops!');
  let details = accountT('An unexpected error occurred.');

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? accountT('404 Not Found') : accountT('Error');
    details = error.status === 404 ? accountT('The requested page could not be found.') : error.statusText || details;
  } else if (error instanceof Error) {
    details = error.message;
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <Text variant="h1">{message}</Text>
      <Text>{details}</Text>
    </main>
  );
}
